import {
  StakeholderType,
  DocumentTypes,
  DocumentTypesMap,
  EnvelopeSubmissionStatus,
  EnvelopeSubmissionType,
  MultiOptionButtonOption,
  QldContractStage,
  NswContractStage,
  FormConfigType,
  EnvelopeRecipientStatus,
  UploadedDocument as OldUploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxDivider,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_CheckmarkCircleOutline,
} from '@urbanx/agx.ui-components2';
import { AgencyAgentModelLicenceType, useGetAgentsInAgency } from '@urbanx/agx.agencies.client';
import { useContext, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { Signatory } from '../../../CampaignsList/components/SentForSigningPanel/Signatory';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { setAndShowErrorToast } from 'store/config';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { getEnumValue } from 'helpers/enumHelper';
import { selectContract, removeFormSubmission } from 'features/campaigns/campaignsReducer';
import { prepareSignatories, getAllBuyerNames } from 'utils/vendorBuyerUtil';
import { formatListOfStrings } from 'utils/formatString';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { useFormLoader } from 'hooks/useFormLoader';
import { Tabs } from 'types/Tabs';
import SendToVendorsConfirmation from './SendToVendorsConfirmation';
import './ContractRequestSigningPanel.scss';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { sortDocumentsByType } from 'utils/documentUtil';
import FileList from '../Common/FileList';
import FileUpload from 'features/elements/FileUpload/FileUpload';
import { docusignApi } from 'Api/DocuSign/docusignApi';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  CoolingOffType,
  InProgressFormModelFormType,
  NoCoolingOffReason,
  UploadedDocument,
  UploadedDocumentDocumentType,
  useExchangeContract,
} from '@urbanx/agx.campaigns.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import { allSigned } from './ContractRequestSigningPanel.helpers';
import { addHorizontalDividersBetweenElements } from '../../Common/addHorizontalDividersBetweenElements';
import { orderEnvelopeRecipients } from '../../Common/orderEnvelopeRecipients';

interface ContractRequestSigningPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
  onPinnedContractChange: (contractId?: string) => void;
}

interface SubmitContractButtonProps {
  disabled: boolean;
  isContractExchange: boolean;
  mobile: boolean;
  tooltip?: string;
  onClick: () => void | Promise<boolean>;
}

const SubmitContractButton = ({
  disabled,
  isContractExchange,
  mobile,
  tooltip,
  onClick,
}: SubmitContractButtonProps) => (
  <AgxButton
    id="startsubmission"
    variant="primary"
    stretch={mobile}
    size="large"
    text={isContractExchange ? 'Exchange Contract' : 'Start Submission'}
    disabled={disabled}
    tooltip={tooltip}
    onClick={onClick}
  />
);

interface ContractActionButtonProps {
  disabled: boolean;
  onClick?: () => void | Promise<unknown>;
}

const DownloadContractExchangeButton = ({ disabled, onClick }: ContractActionButtonProps) => (
  <AgxButton
    id="download-contract-exchange"
    variant="hollow"
    size="large"
    text="Download"
    onClick={onClick}
    disabled={disabled}
  />
);

const DocuSignVendorButton = ({ disabled, onClick }: ContractActionButtonProps) => (
  <AgxButton
    id="send-to-vendors"
    variant="primary"
    size="large"
    text="Send to Vendors"
    onClick={onClick}
    disabled={disabled}
  />
);

const EditContractButton = ({ disabled, onClick }: ContractActionButtonProps) => (
  <AgxButton
    id="edit-contract"
    variant="hollow"
    size="large"
    text="Edit"
    onClick={onClick}
    disabled={disabled}
  />
);

const ContractRequestSigningPanel = ({
  campaign,
  currentTab,
  mobile = false,
  onPinnedContractChange,
}: ContractRequestSigningPanelProps) => {
  const { getAuthToken, userAccount } = useContext(AuthContext);
  const isDesktop = ScreenSize() === Breakpoints.Desktop;
  const { loadForm, loadFormWithConfig } = useFormLoader();
  const selectedContract = useNewSelectedContract();
  const {
    address,
    state,
    propertyImageUrls: { medium: imageUrl = null } = {},
    campaignId,
  } = campaign;
  const { buyers, salePrice, envelopeSubmission, contractStage } = selectedContract ?? {};
  const {
    envelopeSubmissionId: envelopeId,
    status: envelopeSubmissionStatus,
    type: envelopeSubmissionType,
    recipients,
  } = envelopeSubmission ?? {};
  const [disableInputs, setDisableInputs] = useState(false);
  const [disableSendToVendors, setDisableSendToVendors] = useState(false);
  const [showVoidEditConfirmationModal, setShowVoidEditConfirmationModal] = useState(false);
  const [showExchangeConfirmationModal, setShowExchangeConfirmationModal] = useState(false);
  const [waiverOfCoolingOffDocuments, setWaiverOfCoolingOffDocuments] = useState<
    OldUploadedDocument[]
  >([]);
  const [exchangeSourceRevision, setExchangeSourceRevision] = useState<string>();
  const [showSendVendorsConfirmationModal, setShowSendVendorsConfirmationModal] = useState(false);
  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);
  const dispatch = useAppDispatch();
  const { exchangeContract, exchangeContractPending } = useExchangeContract({
    onSuccess: () => {},
    onError: () => {},
  });
  const approver = recipients?.find((r) => r.stakeholderType === StakeholderType.Approver);
  const vendors = prepareSignatories(campaign.vendors, StakeholderType.Vendor);
  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);
  const allVendorSolicitors = prepareSignatories(buyers, StakeholderType.VendorSolicitor);
  const allBuyerSolicitors = prepareSignatories(buyers, StakeholderType.BuyerSolicitor);
  const allSignatories = [...allBuyers, ...vendors, ...allVendorSolicitors, ...allBuyerSolicitors];

  const declinedStatus = recipients?.find(
    (recipient) => recipient.status === EnvelopeRecipientStatus.Declined
  );

  const bouncedStatus = recipients?.find(
    (recipient) => recipient.status === EnvelopeRecipientStatus.Bounced
  );

  const AllBuyersSigned = allSigned(recipients ?? [], StakeholderType.Buyer);
  const AllVendorsSigned = allSigned(recipients ?? [], StakeholderType.Vendor);
  const getFileLink = useGetFileLink();
  const isContractExchange =
    selectedContract?.inProgressForm?.formType === InProgressFormModelFormType.ContractExchange;
  const isNswContractExchange = state === CampaignDetailModelState.NSW && isContractExchange;
  const { data: agencyAgents } = useGetAgentsInAgency(isNswContractExchange);
  const currentAgent = agencyAgents?.find((agent) => agent.id === userAccount?.agent);
  const hasNswClassOneOrTwoLicence =
    currentAgent?.licenceType === AgencyAgentModelLicenceType.Class1 ||
    currentAgent?.licenceType === AgencyAgentModelLicenceType.Class2;
  const disableNswContractExchangeForLicence = isNswContractExchange && !hasNswClassOneOrTwoLicence;
  const contractExchangeLicenceTooltip = disableNswContractExchangeForLicence
    ? 'Only team members with a NSW Class 1 or Class 2 licence can exchange contracts.'
    : undefined;
  const existingWaiverOfCoolingOffDocument = selectedContract?.documents?.find(
    (document) => document.documentType === UploadedDocumentDocumentType.WaiverOfCoolingOff
  );
  const waiverOfCoolingOffRequired =
    selectedContract?.buyerCoolingOffDetails?.coolingOffType === CoolingOffType.NoCoolingOff &&
    selectedContract?.buyerCoolingOffDetails?.noCoolingOffReason === NoCoolingOffReason.Waived;
  const shouldDisplayWaiverOfCoolingOffUpload = isContractExchange && waiverOfCoolingOffRequired;
  const uploadedWaiverOfCoolingOff = waiverOfCoolingOffDocuments.find(
    (document) =>
      document.containerFilePath !== existingWaiverOfCoolingOffDocument?.containerFilePath
  ) as unknown as UploadedDocument | undefined;
  const hasWaiverOfCoolingOffDocument =
    !!existingWaiverOfCoolingOffDocument || !!uploadedWaiverOfCoolingOff;
  const waiverOfCoolingOffForExchange = shouldDisplayWaiverOfCoolingOffUpload
    ? uploadedWaiverOfCoolingOff
    : undefined;
  const exchangeContractDocument = selectedContract?.documents?.find(
    ({ documentType }) =>
      documentType === UploadedDocumentDocumentType.SignedContract ||
      documentType === UploadedDocumentDocumentType.ContractExchange
  );

  const exchangeDocumentRevision =
    exchangeContractDocument &&
    `${exchangeContractDocument.containerFilePath}:${exchangeContractDocument.lastModified}`;

  const exchangeHasNewRevision = Boolean(
    exchangeSourceRevision &&
    exchangeDocumentRevision &&
    exchangeDocumentRevision !== exchangeSourceRevision
  );

  const showExchangeSuccessModal = !exchangeContractPending && exchangeHasNewRevision;

  const exchangeFinalising = Boolean(exchangeSourceRevision) && !showExchangeSuccessModal;

  const fileDownloadOptions = useMemo(() => {
    if ((selectedContract?.documents?.length ?? 0) === 0) return [];
    const options: MultiOptionButtonOption[] = [];
    const miscDocumentTypes = [DocumentTypes.Miscellaneous, DocumentTypes.AdditionalAnnexure];

    const orderedDocuments = sortDocumentsByType(
      selectedContract?.documents as unknown as OldUploadedDocument[]
    );

    const nonMiscDocuments = orderedDocuments.filter(
      (d, ind, arr) =>
        !miscDocumentTypes.includes(d.documentType) &&
        (!ind || d.documentType !== arr[ind - 1]?.documentType)
    );
    const miscDocuments = orderedDocuments.filter((d) =>
      miscDocumentTypes.includes(d.documentType)
    );

    nonMiscDocuments.forEach((d) => {
      options.push({
        text: DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap],
        documentType: d.documentType,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    miscDocuments.forEach((d) => {
      options.push({
        text: d.fileName,
        documentType: d.documentType,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    return options;
  }, [selectedContract?.documents, getFileLink]);

  const contractExchangeDownloadOption =
    fileDownloadOptions.find((file) => file.documentType === DocumentTypes.ContractExchange) ??
    fileDownloadOptions.find((file) => file.documentType !== DocumentTypes.TitleSearch);

  const beginSubmission = async () => {
    try {
      setDisableInputs(true);
      const authToken = await getAuthToken();

      if (!authToken || !selectedContract) {
        setDisableInputs(false);
        return false;
      }

      const newForm = await completeCampaignStep(
        authToken,
        campaignId ?? '',
        false,
        selectedContract.contractId
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableInputs(false);
        return false;
      }

      await loadFormWithConfig(newForm);
      return true;
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
      setDisableInputs(false);
      return false;
    }
  };

  const sendToVendors = async () => {
    setDisableInputs(true);
    setDisableSendToVendors(true);
    try {
      const authToken = await getAuthToken();

      if (!authToken || !selectedContract) return;

      await docusignApi(authToken).post('ApproveEnvelope', {
        campaignId: campaignId,
        formId: selectedContract.contractId,
        envelopeId: envelopeId,
      });
      setShowSendVendorsConfirmationModal(false);
      setDisableInputs(false);
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to send contract to vendors'));
      setDisableInputs(false);
      setDisableSendToVendors(false);
    }
  };

  const allRecipients =
    recipients?.filter(
      (signatory) =>
        signatory.stakeholderType !== StakeholderType.Approver &&
        (!approver || signatory.stakeholderType !== StakeholderType.Vendor)
    ) ?? [];

  const allRecipientsInfo = allRecipients.map((recipient) => {
    const allSignatoriesInfo = allSignatories.find(
      (signatory) => recipient.recipientId === signatory.id
    );

    if (allSignatoriesInfo) {
      return { ...recipient, name: allSignatoriesInfo.name };
    }

    return recipient;
  });
  const orderedRecipientsInfo = orderEnvelopeRecipients(allRecipientsInfo);

  const renderSignatories = () => {
    const signatoryComponents = orderedRecipientsInfo.map((signatory, i) => {
      if (
        signatory &&
        envelopeId &&
        ((envelopeSubmissionType === EnvelopeSubmissionType.DocuSign &&
          (!approver || signatory.stakeholderType !== StakeholderType.Vendor)) ||
          envelopeSubmissionType === EnvelopeSubmissionType.Email)
      ) {
        return (
          <Signatory
            key={`signatory-${i}`}
            signatory={signatory}
            envelopeSubmissionType={envelopeSubmissionType}
            envelopeId={envelopeId}
            declinedStatus={!!(declinedStatus || bouncedStatus)}
          />
        );
      }

      return <></>;
    });

    return [
      <AgxDivider key="initialDivider" variant="horizontal" />,
      ...addHorizontalDividersBetweenElements(signatoryComponents),
    ];
  };

  const onEditContract = async () => {
    if (!selectedContract) return;

    if (envelopeSubmissionType === EnvelopeSubmissionType.Email) {
      await loadForm(campaignId ?? '', selectedContract.contractId ?? '', FormConfigType.Campaign);
    } else if (envelopeSubmissionType === EnvelopeSubmissionType.DocuSign) {
      setShowVoidEditConfirmationModal(true);
    }
  };

  const onVoidAndEdit = async () => {
    if (!selectedContract) return;

    setDisableInputs(true);

    try {
      const authToken = await getAuthToken();

      if (!authToken || !envelopeId) return;

      await removeFormSubmission(
        authToken,
        campaignId ?? '',
        envelopeId,
        selectedContract.contractId
      );

      await loadForm(campaignId ?? '', selectedContract.contractId ?? '', FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to void envelope'));
    }

    setDisableInputs(false);
  };

  const closeExchangeModal = () => {
    setShowExchangeConfirmationModal(false);
    setExchangeSourceRevision(undefined);
    setWaiverOfCoolingOffDocuments([]);
    setDisableInputs(false);
  };

  const completeExchangeContract = async () => {
    if (
      !campaignId ||
      !selectedContract?.contractId ||
      !exchangeDocumentRevision ||
      disableNswContractExchangeForLicence
    )
      return;

    try {
      setDisableInputs(true);
      onPinnedContractChange(selectedContract.contractId);
      setExchangeSourceRevision(exchangeDocumentRevision);

      await exchangeContract({
        campaignId,
        contractId: selectedContract.contractId,
        waiverOfCoolingOff: waiverOfCoolingOffForExchange,
      });

      setDisableInputs(false);
    } catch (err) {
      onPinnedContractChange(undefined);
      setExchangeSourceRevision(undefined);
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to exchange Contract'));
      setDisableInputs(false);
    }
  };

  const startSubmissionFromExchangeSuccess = async () => {
    const submissionStarted = await beginSubmission();

    if (submissionStarted) {
      closeExchangeModal();
    }
  };

  const submitContractDisabled =
    disableInputs ||
    exchangeContractPending ||
    (isContractExchange && !exchangeDocumentRevision) ||
    disableNswContractExchangeForLicence ||
    (envelopeSubmissionType === EnvelopeSubmissionType.DocuSign &&
      !(AllBuyersSigned && AllVendorsSigned)) ||
    (envelopeSubmissionType === EnvelopeSubmissionType.Email &&
      envelopeSubmissionStatus !== EnvelopeSubmissionStatus.Sent);

  const voidEditConfirmationDialogBody = (
    <AgxSpacer direction="vertical" gap="spacing-xl" className="confirmationModalStyle">
      <AgxTypography id="void-edit-contract-description" type="body" size="medium">
        Current recipients will be notified, you'll be able to generate a new contract and send a
        new DocuSign envelope.
      </AgxTypography>
      <AgxTypography id="void-edit-contract-question" type="body" size="medium">
        Continue to edit?
      </AgxTypography>
    </AgxSpacer>
  );

  const voidEditConfirmationDialogActions = (
    <AgxSpacer
      direction="horizontal"
      gap="md"
      contentJustification="center"
      fill
      className="confirmationButtonContainerStyle"
    >
      <AgxButton
        id="startsubmission"
        size="large"
        variant="primary"
        text="Void and Edit"
        onClick={onVoidAndEdit}
        disabled={disableInputs}
        stretch
      />
      <AgxButton
        id="cancel"
        variant="hollow"
        size="large"
        text="No, go back"
        onClick={() => setShowVoidEditConfirmationModal(false)}
        disabled={disableInputs}
        stretch
      />
    </AgxSpacer>
  );

  const confirmSendToVendorsDialogBody = (
    <AgxSpacer direction="vertical" gap="spacing-xl" className="confirmationModalStyle">
      <SendToVendorsConfirmation
        vendors={vendors}
        onBack={() => setShowSendVendorsConfirmationModal(false)}
        onSend={sendToVendors}
        disabled={disableInputs}
      />
    </AgxSpacer>
  );

  const exchangeModalIcon = showExchangeSuccessModal ? (
    <div className="contractExchangeModalIcon success">
      <Icon_CheckmarkCircleOutline width={40} height={40} color="var(--fg-success)" />
    </div>
  ) : (
    <div className="contractExchangeModalIcon warning">
      <Icon_AlertCircleOutline type="Warning" width={40} height={40} />
    </div>
  );

  const exchangeConfirmationContent = showExchangeSuccessModal ? null : (
    <AgxSpacer
      direction="vertical"
      gap="md"
      padding={{ left: 'xl', right: 'xl', bottom: 'spacing-3xl' }}
      className="contractExchangeModalContent"
    >
      <AgxSpacer direction="vertical" gap="spacing-lg">
        <AgxTypography
          id="contract-exchange-deposit-warning"
          type="body_ui"
          size="small"
          color="var(--text-secondary)"
        >
          Deposit must be paid before the Contract can be Exchanged.
        </AgxTypography>
        <AgxTypography
          id="contract-exchange-dated-warning"
          type="body_ui"
          size="small"
          color="var(--text-secondary)"
        >
          Once confirmed, the Contract will be dated.
        </AgxTypography>
      </AgxSpacer>
      {shouldDisplayWaiverOfCoolingOffUpload && (
        <AgxSpacer direction="vertical" gap="spacing-sm" className="contractExchangeWaiverUpload">
          <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-2xs">
            <AgxTypography id="contract-exchange-66w-upload-label" type="body_ui" size="small">
              66W Required to Exchange
            </AgxTypography>
            <AgxTypography
              id="contract-exchange-66w-upload-required"
              type="body_ui"
              size="small"
              color="var(--fg-error)"
            >
              *
            </AgxTypography>
          </AgxSpacer>
          <FileUpload
            id="contract-exchange-66w"
            title="66W Required to Exchange"
            campaignId={campaignId}
            contractId={selectedContract?.contractId}
            documentType={DocumentTypes.WaiverOfCoolingOff}
            extensions={['pdf']}
            fileData={
              existingWaiverOfCoolingOffDocument as unknown as OldUploadedDocument | undefined
            }
            hideLabel
            validate={false}
            required
            onValueChanged={({ value }) => setWaiverOfCoolingOffDocuments(value ?? [])}
          />
        </AgxSpacer>
      )}
    </AgxSpacer>
  );

  const exchangeConfirmationActions = showExchangeSuccessModal ? (
    <AgxButton
      id="start-contract-submission"
      variant="primary"
      size="large"
      text="Start Submission"
      onClick={startSubmissionFromExchangeSuccess}
      disabled={disableInputs}
      stretch
    />
  ) : (
    <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg" fill>
      <AgxButton
        id="cancel-contract-exchange"
        variant="hollow"
        size="large"
        text="Cancel"
        onClick={closeExchangeModal}
        disabled={disableInputs || exchangeContractPending || exchangeFinalising}
        stretch
      />
      <AgxButton
        id="confirm-contract-exchange"
        variant="primary"
        size="large"
        text="Confirm"
        onClick={completeExchangeContract}
        disabled={
          disableInputs ||
          exchangeContractPending ||
          exchangeFinalising ||
          disableNswContractExchangeForLicence ||
          (shouldDisplayWaiverOfCoolingOffUpload && !hasWaiverOfCoolingOffDocument)
        }
        stretch
      />
    </AgxSpacer>
  );

  return (
    <>
      <AgxSpacer direction="vertical" gap="spacing-xl" fill contentAlignment="center">
        <AgxTypography
          id="contract-request-signing-panel-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="contract-request-signing-panel-summary"
          type="body"
          size="medium"
          style={{ textAlign: 'center' }}
        >
          {formatListOfStrings(getAllBuyerNames(allBuyers))} •{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxTypography>
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="center"
          className="listing-imageCard"
        >
          <div
            style={{
              backgroundImage: `url("${imageUrl || placeHolderImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              aspectRatio: '2/1',
            }}
            className={`imageCard-image`}
          >
            <div className="image-tag">
              <AgxTypography
                id="contract-request-signing-panel-stage"
                type="label"
                size="medium"
                color="var(--brand-text-button-color)"
              >
                {state === CampaignDetailModelState.QLD
                  ? getEnumValue(QldContractStage, contractStage as QldContractStage)
                  : getEnumValue(NswContractStage, contractStage as NswContractStage)}
              </AgxTypography>
            </div>
          </div>
        </AgxSpacer>
        <AgxSpacer direction="horizontal" gap="md">
          {isContractExchange && (
            <DownloadContractExchangeButton
              onClick={contractExchangeDownloadOption?.onClick}
              disabled={disableInputs || exchangeContractPending || !contractExchangeDownloadOption}
            />
          )}
          {envelopeSubmissionType !== EnvelopeSubmissionType.Email && (
            <EditContractButton
              onClick={onEditContract}
              disabled={disableInputs || (AllBuyersSigned && AllVendorsSigned)}
            />
          )}
          {!approver && (
            <SubmitContractButton
              mobile={mobile}
              isContractExchange={isContractExchange}
              tooltip={contractExchangeLicenceTooltip}
              onClick={
                isContractExchange ? () => setShowExchangeConfirmationModal(true) : beginSubmission
              }
              disabled={submitContractDisabled}
            />
          )}
          {approver && (
            <DocuSignVendorButton
              onClick={() => setShowSendVendorsConfirmationModal(true)}
              disabled={
                (!AllBuyersSigned && approver !== undefined) ||
                disableSendToVendors ||
                disableInputs
              }
            />
          )}
        </AgxSpacer>

        <AgxSpacer
          direction="vertical"
          fill
          contentAlignment="center"
          className="signatoriesContainer"
        >
          {renderSignatories()}
        </AgxSpacer>
        {!isContractExchange && (
          <FileList
            files={
              state === CampaignDetailModelState.NSW
                ? fileDownloadOptions.filter((file) => file.text !== DocumentTypesMap.TitleSearch)
                : fileDownloadOptions
            }
            title={state === CampaignDetailModelState.QLD ? 'Contract Documents' : 'Sales Advice'}
          />
        )}
      </AgxSpacer>

      {showVoidEditConfirmationModal && (
        <AgxModal
          id="void-edit-contract-modal"
          title="Editing contract will void current DocuSign envelope"
          titleIcon={<Icon_AlertCircleOutline type="Warning" />}
          content={voidEditConfirmationDialogBody}
          actionButtons={voidEditConfirmationDialogActions}
          actionButtonContainerStyle="withoutDivider"
          type="single"
          variant="withoutDivider"
          setIsDialogOpen={setShowVoidEditConfirmationModal}
        />
      )}

      {showSendVendorsConfirmationModal && (
        <AgxModal
          id="send-to-vendors-confirmation-modal"
          title="Vendor Signatories"
          content={confirmSendToVendorsDialogBody}
          actionButtonContainerStyle="withoutDivider"
          type="single"
          variant="withoutDivider"
          setIsDialogOpen={setShowSendVendorsConfirmationModal}
        />
      )}

      {showExchangeConfirmationModal && (
        <AgxModal
          id="contract-exchange-confirmation-modal"
          title={showExchangeSuccessModal ? 'Contract Exchange Complete' : 'Exchange Contract'}
          titleIcon={exchangeModalIcon}
          content={exchangeConfirmationContent}
          actionButtons={exchangeConfirmationActions}
          actionButtonContainerStyle={showExchangeSuccessModal ? 'withoutDivider' : 'withDivider'}
          type={showExchangeSuccessModal ? 'dialog' : 'single'}
          variant="withoutDivider"
          minWidth="min(600px, calc(100vw - 32px))"
          setIsDialogOpen={(isOpen) => {
            if (!isOpen && !exchangeFinalising && !showExchangeSuccessModal) {
              closeExchangeModal();
            }
          }}
        />
      )}
    </>
  );
};

export default ContractRequestSigningPanel;
